import { type ApolloClient } from '@apollo/client';

import { PricingSetFragment, RateGroupKindEnum } from '@graphql/platform';

import { CheckoutType, CheckoutVersion } from '@root/components/checkout/types';
import { PlanName } from '@root/resources/types/plan_name';

import { SafeLocalStorage, SafeSessionStorage } from '@utils/browser';
import { Serializer } from '@utils/serializable';

import { StorageCheckoutData } from '../data';
import { parseCheckoutQueryString } from './checkout_url';
import { updateStorageDraft } from './drafts';

/** Challenger funnel defaults: 8-month minimum + 5′×10′. */
const CHALLENGER_DEFAULT_COMMITMENT = RateGroupKindEnum.Saver;
export const CHALLENGER_DEFAULT_PLAN_SIZE: PlanName = '5x10';

export const withChallengerStorageDefaults = (
  data: StorageCheckoutData,
): StorageCheckoutData => ({
  ...data,
  commitment: data.commitment ?? CHALLENGER_DEFAULT_COMMITMENT,
  planSize: data.planSize ?? CHALLENGER_DEFAULT_PLAN_SIZE,
});

const STORAGE_KEY = '__clutter:checkout_data:storage';
const MOVING_KEY = '__clutter:checkout_data:moving';
const BOOKED_KEY = '__clutter:checkout_data:booked';

const MAX_DURATION = 1000 * 60 * 60; // one hour in milliseconds
const BOOKED_FLAG_DURATION = 1000 * 60 * 15; // 15 minutes in milliseconds

type PersistedData<T> = {
  data: T;
  /** Created/updated timestamp in epoch milliseconds */
  ts: number;
};

type StorageSessionPersistedCheckoutData =
  PersistedData<StorageCheckoutData> & {
    draftId?: string;
    challenger?: boolean;
  };

const serializer = new Serializer();

export const setBookedFlag = () =>
  SafeLocalStorage.setItem(BOOKED_KEY, new Date().toISOString());

export const fetchBookedFlag = () => {
  const value = SafeLocalStorage.getItem(BOOKED_KEY);
  if (value) return Date.parse(value) > Date.now() - BOOKED_FLAG_DURATION;
  return false;
};

export const clearBookedFlag = () => SafeLocalStorage.removeItem(BOOKED_KEY);

export const clearSessionCheckoutData = () => {
  SafeSessionStorage.removeItem(STORAGE_KEY);
  SafeSessionStorage.removeItem(MOVING_KEY);
};

export const persistStorageSessionCheckoutData = (
  client: ApolloClient,
  data: StorageCheckoutData,
  leadToken?: string,
  pricingSet?: PricingSetFragment,
  funnelVersion?: string,
  draftId?: string,
) => {
  const isChallengerFunnel = funnelVersion === CheckoutVersion.StorageV2;
  const serialized = serializer.serialize({
    data,
    ts: Date.now(),
    ...(isChallengerFunnel
      ? { challenger: true, ...(draftId ? { draftId } : {}) }
      : {}),
  });
  updateStorageDraft(client, data, leadToken, pricingSet, funnelVersion);
  SafeSessionStorage.setItem(STORAGE_KEY, serialized);
};

const readStorageSessionSnapshot = ():
  | StorageSessionPersistedCheckoutData
  | undefined => {
  const snapshot = serializer.deserialize<StorageSessionPersistedCheckoutData>(
    SafeSessionStorage.getItem(STORAGE_KEY) || '{}',
  );

  if ((snapshot.ts ?? 0) + MAX_DURATION > Date.now()) return snapshot;
};

/** Same-tab checkout snapshot, if one was persisted within the TTL. */
export const peekStorageSessionCheckoutData = ():
  | StorageCheckoutData
  | undefined => readStorageSessionSnapshot()?.data;

/** Overlay defined session fields onto draft-restored values (same-tab resume). */
export const overlayStorageCheckoutData = (
  base: StorageCheckoutData,
  overlay: StorageCheckoutData | undefined,
): StorageCheckoutData => {
  if (!overlay) return base;
  const definedEntries = Object.entries(overlay).filter(
    ([, value]) => value !== undefined,
  );
  return {
    ...base,
    ...Object.fromEntries(definedEntries),
  };
};

type RestoreStorageSessionOptions = {
  applyChallengerDefaults?: boolean;
};

export const restoreStorageSessionCheckoutData = (
  options?: RestoreStorageSessionOptions,
): Omit<StorageSessionPersistedCheckoutData, 'ts' | 'draftId'> => {
  const applyChallengerDefaults = options?.applyChallengerDefaults ?? false;
  const snapshot = readStorageSessionSnapshot();
  const data = snapshot?.data;

  if (data) {
    if (applyChallengerDefaults) {
      return { data: withChallengerStorageDefaults(data) };
    }
    return { data: { ...data } };
  }

  const { zip, zipValidated } = parseCheckoutQueryString(
    window.location.search,
  );

  const emptyData: StorageCheckoutData = {
    checkoutType: CheckoutType.Standard,
    zip,
    zipValidated,
  };

  if (applyChallengerDefaults) {
    return {
      data: withChallengerStorageDefaults(emptyData),
    };
  }

  return { data: emptyData };
};
